<script setup lang="ts">
  import { computed } from 'vue'
  import { Badge, type BadgeVariants } from '@/components/ui/badge'
  import {
    FileText,
    Mail,
    Phone,
    FileQuestion,
    FileText as Document,
    Sparkles,
    MessageSquare,
    Upload,
    Paperclip,
    Calendar,
    FileInput
  } from 'lucide-vue-next'
  import { DocumentTypeEnum, UploadOriginEnum } from '@/types/generated'
  import {
    TooltipProvider,
    Tooltip,
    TooltipTrigger,
    TooltipContent
  } from '@/components/ui/tooltip/'

  const props = withDefaults(
    defineProps<{
      type: DocumentTypeEnum | string | null
      showIcon?: boolean
      showText?: boolean
    }>(),
    {
      showIcon: true,
      showText: true
    }
  )

  const badgeClasses = computed(() => {
    return 'inline-flex items-center gap-1.5 text-sm py-1 px-2 rounded-md w-auto whitespace-nowrap'
  })

  const badgeVariant = computed<BadgeVariants['variant']>(() => {
    return 'outline'
  })

  const badgeCustomClass = computed(() => {
    switch (props.type) {
      case DocumentTypeEnum.ANFRAGE:
        return 'bg-amber-50 text-amber-800 border-amber-200'
      case 'Anfrage Notiz':
        return 'bg-amber-50 text-amber-800 border-amber-200'
      case DocumentTypeEnum.E_MAIL:
        return 'bg-orange-50 text-orange-800 border-orange-200'
      case DocumentTypeEnum.TRANSKRIPT:
        return 'bg-green-50 text-green-800 border-green-200'
      case DocumentTypeEnum.FRAGEBOGEN:
        return 'bg-cyan-50 text-cyan-800 border-cyan-200'
      case DocumentTypeEnum.DOKUMENTENVORLAGE:
        return 'bg-blue-50 text-blue-800 border-blue-200'
      case DocumentTypeEnum.KI_GENERIERTES_DOKUMENT:
        return 'bg-purple-50 text-purple-800 border-purple-200'
      case DocumentTypeEnum.KI_CHAT:
        return 'bg-purple-50 text-purple-800 border-purple-200'
      case DocumentTypeEnum.UPLOAD:
        return 'bg-gray-50 text-gray-800 border-gray-200'
      default:
        return 'bg-gray-50 text-gray-800 border-gray-200'
    }
  })

  const badgeText = computed(() => {
    switch (props.type) {
      case DocumentTypeEnum.ANFRAGE:
        return 'Inhalt der Anfrage'
      case 'Anfrage Notiz':
        return 'Anfrage Notiz'
      case DocumentTypeEnum.E_MAIL:
        return 'E-Mail'
      case DocumentTypeEnum.TRANSKRIPT:
        return 'Anruf Transkript'
      case DocumentTypeEnum.FRAGEBOGEN:
        return 'Fragebogen'
      case DocumentTypeEnum.DOKUMENTENVORLAGE:
        return 'Dokumentenvorlage'
      case DocumentTypeEnum.KI_GENERIERTES_DOKUMENT:
        return 'KI Dokument'
      case DocumentTypeEnum.KI_CHAT:
      case UploadOriginEnum.AI_CASE_CHAT:
        return 'KI Chat'
      case UploadOriginEnum.CHATBOT_UPLOAD:
        return 'Chatbot Upload'
      case UploadOriginEnum.EMAIL_ANHANG:
        return 'Email Anhang'
      case UploadOriginEnum.TERMINBUCHUNG:
        return 'Terminbuchung'
      case UploadOriginEnum.MANUELL_HINZUGEFÜGT:
        return 'Manuell hinzugefügt'
      case UploadOriginEnum.EXTERNAL_PLATFORM:
        return 'Externe Plattform'
      case UploadOriginEnum.NONE:
        return 'Upload'
      default:
        return props.type?.toString() || ''
    }
  })

  const badgeIcon = computed(() => {
    switch (props.type) {
      case DocumentTypeEnum.ANFRAGE:
      case 'Anfrage Notiz':
        return FileText
      case DocumentTypeEnum.E_MAIL:
        return Mail
      case DocumentTypeEnum.TRANSKRIPT:
        return Phone
      case DocumentTypeEnum.FRAGEBOGEN:
        return FileQuestion
      case DocumentTypeEnum.DOKUMENTENVORLAGE:
        return Document
      case DocumentTypeEnum.KI_GENERIERTES_DOKUMENT:
        return Sparkles
      case DocumentTypeEnum.KI_CHAT:
      case UploadOriginEnum.AI_CASE_CHAT:
        return MessageSquare
      case DocumentTypeEnum.UPLOAD:
      case UploadOriginEnum.CHATBOT_UPLOAD:
      case UploadOriginEnum.EMAIL_ANHANG:
      case UploadOriginEnum.MANUELL_HINZUGEFÜGT:
        return Upload
      case UploadOriginEnum.TERMINBUCHUNG:
        return Calendar
      case UploadOriginEnum.EXTERNAL_PLATFORM:
        return FileInput
      case UploadOriginEnum.NONE:
      default:
        return Paperclip
    }
  })
</script>

<template>
  <Badge
    :variant="badgeVariant"
    :class="[badgeClasses, badgeCustomClass, 'h-7']"
  >
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger class="cursor-default">
          <component
            :is="badgeIcon"
            v-if="showIcon"
            class="size-4"
          />
        </TooltipTrigger>
        <TooltipContent v-if="!showText">
          {{ badgeText }}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
    <template v-if="showText">{{ badgeText }}</template>
  </Badge>
</template>
